import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { resellerBranding } from '@/api/resellerBranding';
import type { ResellerBrandingRow, PutBrandingBody } from '@/api/resellerBranding';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Palette, Save, ImageOff, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Draft {
  brand_name: string;
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  email_from_name: string;
  email_from_addr: string;
  sms_sender_name: string;
  kb_welcome_md: string;
}

const EMPTY_DRAFT: Draft = {
  brand_name: '',
  logo_url: '',
  favicon_url: '',
  primary_color: '',
  secondary_color: '',
  email_from_name: '',
  email_from_addr: '',
  sms_sender_name: '',
  kb_welcome_md: '',
};

function fromRow(row: ResellerBrandingRow | null): Draft {
  if (!row) return EMPTY_DRAFT;
  return {
    brand_name: row.brand_name ?? '',
    logo_url: row.logo_url ?? '',
    favicon_url: row.favicon_url ?? '',
    primary_color: row.primary_color ?? '',
    secondary_color: row.secondary_color ?? '',
    email_from_name: row.email_from_name ?? '',
    email_from_addr: row.email_from_addr ?? '',
    sms_sender_name: row.sms_sender_name ?? '',
    kb_welcome_md: row.kb_welcome_md ?? '',
  };
}

function toBody(d: Draft): PutBrandingBody {
  const empty = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    brand_name: d.brand_name.trim(),
    logo_url: empty(d.logo_url),
    favicon_url: empty(d.favicon_url),
    primary_color: empty(d.primary_color),
    secondary_color: empty(d.secondary_color),
    email_from_name: empty(d.email_from_name),
    email_from_addr: empty(d.email_from_addr),
    sms_sender_name: empty(d.sms_sender_name),
    kb_welcome_md: empty(d.kb_welcome_md),
  };
}

export function BrandingPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['reseller-branding'],
    queryFn: () => resellerBranding.get(),
  });

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);

  const loaded = data?.branding ?? null;

  useEffect(() => {
    if (data) setDraft(fromRow(data.branding));
  }, [data]);

  const save = useMutation({
    mutationFn: () => resellerBranding.put(toBody(draft)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reseller-branding'] });
      setError(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : t('common.failedToSave'));
    },
  });

  const dirty = useMemo(() => {
    const baseline = fromRow(loaded);
    return (Object.keys(draft) as (keyof Draft)[]).some((k) => draft[k] !== baseline[k]);
  }, [draft, loaded]);

  function update<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  return (
    <>
      <PageHeader
        eyebrow={t('navGroups.settings')}
        title={t('branding.title')}
        description={t('branding.description')}
      />

      {isLoading ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_24rem]">
          <div className="space-y-6">
            <Section title={t('branding.identitySection')} description={t('branding.identityDescription')}>
              <Field label={t('branding.brandName')} required>
                <Input
                  value={draft.brand_name}
                  onChange={(e) => update('brand_name', e.target.value)}
                  placeholder="e.g. Acme Telecom"
                />
              </Field>
              <Field label={t('branding.logoUrl')} hint={t('branding.logoHint')}>
                <Input
                  value={draft.logo_url}
                  onChange={(e) => update('logo_url', e.target.value)}
                  placeholder="https://cdn.example.com/logo.svg"
                />
              </Field>
              <Field label={t('branding.faviconUrl')}>
                <Input
                  value={draft.favicon_url}
                  onChange={(e) => update('favicon_url', e.target.value)}
                  placeholder="https://cdn.example.com/favicon.ico"
                />
              </Field>
            </Section>

            <Section title={t('branding.paletteSection')} description={t('branding.paletteDescription')} icon={Palette}>
              <div className="grid grid-cols-2 gap-4">
                <ColorField
                  label={t('branding.primaryColor')}
                  value={draft.primary_color}
                  onChange={(v) => update('primary_color', v)}
                />
                <ColorField
                  label={t('branding.secondaryColor')}
                  value={draft.secondary_color}
                  onChange={(v) => update('secondary_color', v)}
                />
              </div>
            </Section>

            <Section title={t('branding.messagingSection')} description={t('branding.messagingDescription')}>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t('branding.emailFromName')}>
                  <Input
                    value={draft.email_from_name}
                    onChange={(e) => update('email_from_name', e.target.value)}
                    placeholder="Acme Support"
                  />
                </Field>
                <Field label={t('branding.emailFromAddress')}>
                  <Input
                    type="email"
                    value={draft.email_from_addr}
                    onChange={(e) => update('email_from_addr', e.target.value)}
                    placeholder="support@example.com"
                  />
                </Field>
              </div>
              <Field label={t('branding.smsSenderName')} hint={t('branding.smsSenderHint')}>
                <Input
                  value={draft.sms_sender_name}
                  onChange={(e) => update('sms_sender_name', e.target.value)}
                  placeholder="AcmeTel"
                  maxLength={64}
                />
              </Field>
            </Section>

            <Section title={t('branding.kbWelcomeSection')} description={t('branding.kbWelcomeDescription')}>
              <Textarea
                rows={5}
                value={draft.kb_welcome_md}
                onChange={(e) => update('kb_welcome_md', e.target.value)}
                placeholder="# Welcome to Acme Support&#10;&#10;If you need help, call us at…"
                className="font-mono text-xs"
              />
            </Section>

            {error && (
              <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="sticky bottom-0 -mx-6 border-t border-border bg-background/90 px-6 py-3 backdrop-blur flex items-center justify-between">
              <span className={cn(
                'text-xs',
                dirty ? 'text-amber-600 font-medium' : 'text-muted-foreground',
              )}>
                {dirty ? t('branding.unsavedChanges') : t('branding.allChangesSaved')}
              </span>
              <div className="flex gap-2">
                <Button
                  type="button" variant="outline" size="sm"
                  disabled={!dirty || save.isPending}
                  onClick={() => { setDraft(fromRow(loaded)); setError(null); }}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  {t('branding.discard')}
                </Button>
                <Button
                  type="button" size="sm"
                  disabled={!dirty || !draft.brand_name.trim() || save.isPending}
                  onClick={() => save.mutate()}
                >
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  {save.isPending ? t('common.saving') : t('branding.saveBranding')}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-6 lg:self-start">
            <BrandPreview draft={draft} />
          </div>
        </div>
      )}
    </>
  );
}

function Section({
  title, description, icon: Icon, children,
}: {
  title: string; description?: string; icon?: typeof Palette;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          <CardTitle className="text-sm">{title}</CardTitle>
        </div>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function Field({
  label, hint, required, children,
}: {
  label: string; hint?: string; required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label>
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ColorField({
  label, value, onChange,
}: {
  label: string; value: string; onChange: (v: string) => void;
}) {
  const [picker, setPicker] = useState(value && value.startsWith('#') ? value : '#1f2937');
  useEffect(() => {
    if (value && value.startsWith('#')) setPicker(value);
  }, [value]);

  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <div className="relative h-9 w-9 shrink-0 rounded-md overflow-hidden ring-1 ring-border">
          <input
            type="color"
            aria-label={`${label} color picker`}
            value={picker}
            onChange={(e) => {
              setPicker(e.target.value);
              onChange(e.target.value);
            }}
            className="absolute inset-0 h-full w-full cursor-pointer border-0 p-0"
          />
        </div>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#0f172a"
          className="font-mono text-sm"
        />
      </div>
    </div>
  );
}

function BrandPreview({ draft }: { draft: Draft }) {
  const { t } = useTranslation();
  const primary = draft.primary_color || '#0f172a';
  const secondary = draft.secondary_color || '#64748b';
  const name = draft.brand_name.trim() || 'Your Brand';

  return (
    <Card
      className="overflow-hidden"
      style={{ '--brand-primary': primary, '--brand-secondary': secondary } as React.CSSProperties}
    >
      <CardHeader className="bg-gradient-to-br from-slate-50 to-white border-b border-border">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('branding.livePreview')}
        </CardTitle>
        <CardDescription className="text-xs">
          {t('branding.previewDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="border-b border-border p-5" style={{ backgroundColor: primary }}>
          <div className="flex items-center gap-3">
            {draft.logo_url ? (
              <img
                src={draft.logo_url}
                alt=""
                className="h-8 w-8 rounded bg-white/10 object-contain p-0.5"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="h-8 w-8 rounded bg-white/10 flex items-center justify-center">
                <ImageOff className="h-3.5 w-3.5 text-white/50" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-semibold text-white truncate">{name}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/60">
                {t('branding.portal')}
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <div className="space-y-1.5">
            <div className="h-2.5 w-24 rounded-full bg-slate-200" />
            <div className="h-2 w-32 rounded-full bg-slate-100" />
          </div>
          <button
            type="button"
            className="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-opacity hover:opacity-90"
            style={{ backgroundColor: primary }}
          >
            Primary action
          </button>
          <button
            type="button"
            className="inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium ml-1 transition-colors hover:opacity-80"
            style={{ color: secondary, borderColor: secondary }}
          >
            Secondary
          </button>

          <div className="pt-3 mt-3 border-t border-dashed border-border">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
              {t('branding.sampleEmailSignature')}
            </div>
            <div className="text-xs">
              <span className="font-medium">{draft.email_from_name || 'From name'}</span>
              <span className="text-muted-foreground"> · </span>
              <span className="font-mono text-muted-foreground">
                {draft.email_from_addr || 'from@example.com'}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
